import type {
  APIBookingDraftCarRentalService,
  APIBookingDraftCarRentalServiceOption,
  APIBookingDraftServiceOptionParticipantPrice,
  ServiceRenderType,
} from '@/scopes/checkout/infrastructure/api/types'
import type { IconGlyph } from '@sunwebgroup/ui-components/uds'
import type {
  CheckoutCarRentalServiceOption,
  CheckoutCarRentalServiceOptionFeature,
} from '../../../domain/types'
import { partial, pipe, prop } from 'ramda'

export interface ServiceCarRentalOptionMapper {
  map: (
    option: APIBookingDraftCarRentalServiceOption,
    service: APIBookingDraftCarRentalService
  ) => CheckoutCarRentalServiceOption

  mapUpgradeOption: (
    option: APIBookingDraftCarRentalServiceOption,
    service: APIBookingDraftCarRentalService,
    selectedCarPrice: number
  ) => CheckoutCarRentalServiceOption
}

export function createServiceCarRentalOptionMapper() {
  function map(option: APIBookingDraftCarRentalServiceOption, service: APIBookingDraftCarRentalService) {
    const imageUrlLarge = isValidUrl(option.imagePath?.large ?? '') ? option.imagePath?.large : ''
    const imageUrlSmall = isValidUrl(option.imagePath?.small ?? '') ? option.imagePath?.small : ''

    const carOption: CheckoutCarRentalServiceOption = {
      id: option.uniqueId ?? option.code,
      value: option.code,
      price: mapOptionPrice(option.price, option.prices ?? [], service.isPricePerPerson ?? false, service.renderType),
      image: imageUrlLarge ?? imageUrlSmall ?? '',
      carClass: mapCarClass(option),
      label: option.carModel ?? '',
      conditionsUrl: option.conditionsUrl ?? '',
      features: mapFeatures(option) ?? [],
      extraFeatures: mapExtraFeatures(option) ?? [],
      equipments: mapEquipments(option) ?? [],
      selectedEquipments: option.selectedEquipments?.map(prop('id')) ?? [],
      selectedEquipmentsWithQuantity:
        option.selectedEquipments?.map(equipment => ({
          id: equipment.id,
          quantity: equipment.quantitySelected ?? 1,
        })) ?? [],
      maxNumberOfEquipments: option.maxNumberOfEquipments ?? 0,
      driverId: option.driverId === 0 ? null : String(option.driverId),
      location: {
        icon: option.locationCode === 'AirportToAirport' ? ('AirplaneToTop' as IconGlyph) : null,
        text: option.location || '',
      },
      openJawFeeAmount: option.openJawFeeAmount ?? 0,
      availableOnRequest: option.availableOnRequest ?? false,
      canBeRemoved: option.isCancellable ?? false,
      canBeEdited: option.isSelfEditable ?? false,
      canBeUpgraded: option.canBeUpgraded ?? false,
      isIncluded: option.isIncluded ?? false,
    }
    return carOption
  }

  function mapCarClass(option: APIBookingDraftCarRentalServiceOption) {
    const transmissionFeature = option.features?.find(feature => feature.type === 'carClass')
    if (transmissionFeature) {
      return transmissionFeature.value.join(', ')
    }
    if (option.carClass) {
      return option.carClass
    }
    return ''
  }

  const getPriceSymbol = (priceDifference: number) => {
    if (priceDifference > 0) {
      return '+'
    }
    if (priceDifference < 0) {
      return '-'
    }
    return ''
  }

  function mapUpgradeOption(
    option: APIBookingDraftCarRentalServiceOption,
    service: APIBookingDraftCarRentalService,
    selectedCarPrice: number,
  ) {
    const mappedOption = map(option, service)
    const priceDifference = option.price - selectedCarPrice

    return { ...mappedOption, price: Math.abs(priceDifference), priceSymbol: getPriceSymbol(priceDifference) }
  }

  function mapOptionPrice(
    price: number,
    prices: APIBookingDraftServiceOptionParticipantPrice[],
    isPricePerPerson: boolean,
    renderType: ServiceRenderType,
  ): number {
    const pricesWithParticipantsFiltered = prices.filter(itemPrice => itemPrice.participantIds.length > 0)

    if (
      !isPricePerPerson
      || (isPricePerPerson && prices.length === 0)
      || (renderType === 'PerParticipant'
        && isPricePerPerson
        && prices.length > 0
        && pricesWithParticipantsFiltered.length === 1)
    ) {
      return price
    }

    return prices?.reduce((total, item) => total + item.price * item.participantIds.length, 0)
  }

  function mapEquipments(option: APIBookingDraftCarRentalServiceOption): { id: string, text: string }[] {
    const { equipments } = option

    return (equipments || []).map(equipment => ({
      id: equipment.id,
      text: equipment.text,
      price: equipment.price ?? 0,
      maxQuantity: equipment.maxQuantity ?? 1,
    }))
  }

  return { map, mapUpgradeOption }
}

export function isValidUrl(urlString: string) {
  let url
  try {
    url = new URL(urlString)
  }
  catch {
    return false
  }
  return url.protocol === 'http:' || url.protocol === 'https:'
}

export function mapFeatures(option: APIBookingDraftCarRentalServiceOption) {
  return pipe(
    partial(mapPassengersFeature, [option]),
    partial(mapLuggageFeature, [option]),
    partial(mapDoorsFeature, [option]),
  )([])
}

export function mapExtraFeatures(option: APIBookingDraftCarRentalServiceOption) {
  return pipe(
    partial(mapTransmissionFeature, [option]),
    partial(mapAdditionalServicesFeature, [option]),
    partial(mapPickupLocationFeature, [option]),
    partial(mapDepositPaymentMethodFeature, [option]),
    partial(mapOtherFeatures, [option]),
  )([])
}

function mapPassengersFeature(
  option: APIBookingDraftCarRentalServiceOption,
  features: CheckoutCarRentalServiceOptionFeature[],
) {
  if (!option.passengers) {
    return features
  }
  return [...features, { glyph: 'Person' as IconGlyph, text: option.passengers?.toString() }]
}

function mapLuggageFeature(
  option: APIBookingDraftCarRentalServiceOption,
  features: CheckoutCarRentalServiceOptionFeature[],
) {
  if (!option.luggage) {
    return features
  }
  return [...features, { glyph: 'Suitcase' as IconGlyph, text: option.luggage?.toString() }]
}

function mapDoorsFeature(
  option: APIBookingDraftCarRentalServiceOption,
  features: CheckoutCarRentalServiceOptionFeature[],
) {
  if (!option.doors) {
    return features
  }
  return [...features, { glyph: 'CarDoor' as IconGlyph, text: option.doors?.toString() }]
}

function mapTransmissionFeature(
  option: APIBookingDraftCarRentalServiceOption,
  features: CheckoutCarRentalServiceOptionFeature[],
) {
  const transmissionFeature = option.features?.find(feature => feature.type === 'transmission')
  if (!transmissionFeature) {
    return features
  }

  const allValues: string[] = transmissionFeature.value.map(item =>
    typeof item === 'string' ? item : item.text,
  )

  return [...features, {
    glyph: 'ManualTransmission' as IconGlyph,
    values: allValues,
  }]
}

function mapAdditionalServicesFeature(
  option: APIBookingDraftCarRentalServiceOption,
  features: CheckoutCarRentalServiceOptionFeature[],
) {
  const additionalServicesFeature = option.features?.find(feature => feature.type === 'additionalServices')
  if (!additionalServicesFeature) {
    return features
  }

  const mappedValues = additionalServicesFeature.value.map((item) => {
    if (typeof item === 'string') {
      return { glyph: 'PlusInSquare' as IconGlyph, values: [item] }
    }
    return { glyph: item.code === 'AC' ? 'Snowflake' : 'InformationInCircle' as IconGlyph, values: [item.text] }
  })

  return [...features, ...mappedValues]
}

function mapPickupLocationFeature(
  option: APIBookingDraftCarRentalServiceOption,
  features: CheckoutCarRentalServiceOptionFeature[],
) {
  const pickupLocationFeature = option.features?.find(feature => feature.type === 'pickUpLocation')
  if (!pickupLocationFeature) {
    return features
  }
  const allValues: string[] = pickupLocationFeature.value.map(item =>
    typeof item === 'string' ? item : item.text,
  )

  return [...features, {
    glyph: 'MapPin' as IconGlyph,
    values: allValues,
  }]
}

function mapDepositPaymentMethodFeature(
  option: APIBookingDraftCarRentalServiceOption,
  features: CheckoutCarRentalServiceOptionFeature[],
) {
  const depositPaymentMethodFeature = option.features?.find(feature => feature.type === 'depositPaymentMethod')
  if (!depositPaymentMethodFeature) {
    return features
  }

  const allValues: string[] = depositPaymentMethodFeature.value.map(item =>
    typeof item === 'string' ? item : item.text,
  )

  return [...features, {
    glyph: 'PaymentCard' as IconGlyph,
    values: allValues,
  }]
}

function mapOtherFeatures(
  option: APIBookingDraftCarRentalServiceOption,
  features: CheckoutCarRentalServiceOptionFeature[],
) {
  const otherFeature = option.features?.find(feature => feature.type === 'other')
  if (!otherFeature) {
    return features
  }

  const allValues: string[] = otherFeature.value.map(item =>
    typeof item === 'string' ? item : item.text,
  )

  return [...features, {
    glyph: 'InformationInCircle' as IconGlyph,
    values: allValues,
  }]
}
